import React, { PureComponent } from 'react';
import Fragment from 'react-dot-fragment';
import { Loader } from 'react-loaders';

import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

import { batchRequest, getRequest } from '../../../../../../api/ApiService';
import Auth from '../../../../../../routes/Auth';

import { handleSelectChange, arrayUniqueFilter } from '../../../utils';

import BannerLayout from '../../../layouts/banner';

class MarketShareStep7 extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      loaded: false,
      teamsToShow: [],
      selectedTeamId: 0,
      allTeamsCount: 0,
      totalUnitsSold: 0,
      otherTeamsUnitsSold: 0,
      otherTeamsUnitsSoldPercent: 0,
      teamUnits: 0,
      teamUnitsPercent: 0,
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  numberWithCommas(x) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  renderLabel = (props) => {
    const {
      cx, cy, midAngle, outerRadius, value, name, index, payload,
    } = props;
    const { originalValue } = payload;
    const { totalUnitsSold } = this.state;
    const RADIAN = Math.PI / 180;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 30) * cos;
    const my = cy + (outerRadius + 30) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 35;
    const ey = my;
    const textAnchor = cos >= 0 ? 'start' : 'end';

    return (
      <g>
        {index === 0 && (
          <text
            x={cx}
            y={cy}
            dy={0}
            textAnchor="middle"
            className="chartCenterLabel"
          >Total Units</text>
        )}
        {index === 0 && (
          <text
            x={cx}
            y={cy}
            dy={20}
            textAnchor="middle"
            className="chartCenterLabel"
          >{Number((totalUnitsSold / 1000000).toFixed(2))}M</text>
        )}
        <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke="rgba(0, 27, 59, 0.94)" fill="none" />
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 20}
          y={ey}
          dy={-10}
          textAnchor={textAnchor}
          className="chartLabel"
        >{`${value}%`}</text>
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 20}
          y={ey}
          dy={10}
          textAnchor={textAnchor}
          className="chartDescription1"
        >{this.numberWithCommas(originalValue)}</text>
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 20}
          y={ey}
          dy={32}
          textAnchor={textAnchor}
          className="chartDescription2"
        >{name}</text>
      </g>
    );
  }

  fetchData = () => {
    this.setState({
      loaded: false
    });

    const { activeRound, rounds, performanceFeedbacksGame } = this.props;

    getRequest(
      `${process.env.REACT_APP_API_URL}teams/`
    ).then(res => {
      const teamsToShowIds = performanceFeedbacksGame.find(i => i.type === "Low inventory").teams.filter(arrayUniqueFilter);

      const teams = res.body;
      const teamsToShow = teams.filter(i => teamsToShowIds.includes(i.id));

      const temporaryId = Auth.getTeamId();
      const team_id = teamsToShowIds.includes(temporaryId)
        ? temporaryId
        : teamsToShowIds[0];

      const round = rounds.find(i => Number(i.number) === Number(activeRound));
      const roundId = round ? round.id : 0;

      batchRequest([
        `${process.env.REACT_APP_API_URL}v2/finance/metricsreports/?round=${roundId}`,
        `${process.env.REACT_APP_API_URL}team/inventory_balances/?round_number=${activeRound}&team_id=${team_id}`,
      ]).then(res => {
        const metrics = res[0].body.results;

        const competition_id = metrics.find(i => i.team === team_id).competition;

        const metricsreportsWithCompetitionId = metrics.filter(i => i.competition === competition_id);
        const totalUnitsSold = metricsreportsWithCompetitionId
          .map(i => Math.abs(Number(i['units_sold'] || 0)))
          .reduce((prev, curr) => prev + curr, 0);
        const otherTeamsUnitsSold = metricsreportsWithCompetitionId
          .filter(i => Number(i.team) !== team_id)
          .map(i => Math.abs(Number(i['units_sold'] || 0)))
          .reduce((prev, curr) => prev + curr, 0);
        const inventoryBalances = res[1].body;
        const teamUnits = inventoryBalances
          .map(i => Math.abs(Number(i.available_for_sale || 0)))
          .reduce((prev, curr) => prev + curr, 0);

        const otherTeamsUnitsSoldPercent = otherTeamsUnitsSold || teamUnits
          ? Number((otherTeamsUnitsSold / (otherTeamsUnitsSold + teamUnits) * 100).toFixed(2))
          : 0;
        const teamUnitsPercent = Number((100 - otherTeamsUnitsSoldPercent).toFixed(2));

        this.setState({
          loaded: true,
          teamsToShow,
          selectedTeamId: Number(team_id),
          allTeamsCount: teams.length,
          totalUnitsSold,
          otherTeamsUnitsSold,
          otherTeamsUnitsSoldPercent,
          teamUnits,
          teamUnitsPercent,
        })
      })
        .catch(err => { });
    })
      .catch(err => { });
  }

  render() {
    const {
      loaded,
      teamsToShow,
      selectedTeamId,
      allTeamsCount,
      otherTeamsUnitsSold,
      otherTeamsUnitsSoldPercent,
      teamUnits,
      teamUnitsPercent,
    } = this.state;

    const { previousShownErrors } = this.props;

    return loaded ? (
      <BannerLayout
        section="Why Targets Weren’t Hit"
        subSection="Market share"
        alertText={`${teamsToShow.length} out of ${allTeamsCount} teams had insufficient capacity.`}
        previousShownErrors={previousShownErrors}
        paragraph2={(
          <Fragment>
            <p>These teams did not produce enough inventory to hit their market share target and this could be due to either Ops not understanding how production impacts market share capture and/or how creating a higher quality product or selecting the cheapest machines impact production capacity.</p>
            <ul>
              <li>The higher the product’s quality, the longer the design release date,  the less time available in the year to manufacture the products.</li>
              <li>Cheaper machines are less productive.</li>
            </ul>
          </Fragment>
        )}
        selectorOptions={teamsToShow}
        selectorSelectedOptionId={selectedTeamId}
        onSelectorChange={value => handleSelectChange(value, this.fetchData)}
        visual={(
          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <Pie
                data={[
                  { name: 'Units sold (other teams)', originalValue: otherTeamsUnitsSold, value: otherTeamsUnitsSoldPercent },
                  { name: 'Team\'s units available for sale', originalValue: teamUnits, value: teamUnitsPercent }
                ]}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                fill="#8884d8"
                dataKey="value"
                label={this.renderLabel}
                labelLine={false}
                startAngle={90}
                endAngle={450}
              >
                ${['#52c5cd', '#f9c151'].map((color, index) => (
                  <Cell key={`pie-cell-${index}`} fill={color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        )}
        visualProps={{
          noBorder: true,
          noBackground: true,
        }}
      />
    ) : <Loader type="ball-scale-ripple" active />;
  }
}

export default MarketShareStep7;



// WEBPACK FOOTER //
// ./src/instructor/components/performance-feedback/pages/why-targets-werent-hit/market-share/step7.js